import { useCallback, useState } from 'react'

const STORAGE_PREFIX = 'panel-expanded:'

/** Read this panel's persisted expanded flag. Defaults to false (compact). */
function readStored(key: string): boolean {
  try {
    return localStorage.getItem(STORAGE_PREFIX + key) === '1'
  } catch {
    /* Storage may throw in private-browse modes — fall through. */
  }
  return false
}

/**
 * Per-panel compact/expanded state for feed/list cards.
 *
 * Compact caps the panel's scroll region at `--panel-h-compact` (300px);
 * expanded lifts it to `--panel-h-expanded` (70vh). Both scroll internally,
 * so a panel never pushes the page. The choice is persisted to localStorage
 * under `panel-expanded:<key>` so it survives reloads.
 *
 * `scrollClassName` is applied to the panel's scrollable element (a `<ul>`,
 * or a `<div>` wrapping a `<table>`).
 * @param key  Stable per-panel identifier; must not change between renders.
 */
export function useExpandablePanel(key: string): {
  expanded: boolean
  toggle: () => void
  scrollClassName: string
} {
  const [expanded, setExpanded] = useState<boolean>(() => readStored(key))

  const toggle = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev
      try {
        localStorage.setItem(STORAGE_PREFIX + key, next ? '1' : '0')
      } catch {
        /* ignore persistence failure — in-memory state still flips */
      }
      return next
    })
  }, [key])

  const scrollClassName = expanded ? 'panel-scroll is-expanded' : 'panel-scroll'
  return { expanded, toggle, scrollClassName }
}
